import type { FileRecord } from "@cohesive/api-client";

export interface CopyPlanEntry {
  /** Source record to copy. */
  fileRecordId: string;
  /** Destination path, including the trailing slash for folder markers. */
  path: string;
}

/**
 * Every copy a folder transfer has to issue. The server copies one row per
 * call — it has no notion of a folder's contents — so a folder copy is the
 * marker row plus each descendant, re-rooted under the destination.
 *
 * Folders come first so a marker always exists before anything lands inside
 * it. `destinationFolderPath` is the target's parent prefix (`""` for the
 * workspace root, otherwise ending in `/`), and `leaf` is the name the copy
 * takes there — already de-duplicated by the caller.
 */
export function buildFolderCopyPlan(params: {
  folder: FileRecord;
  allRecords: FileRecord[];
  destinationFolderPath: string;
  leaf: string;
}): CopyPlanEntry[] {
  const { folder, allRecords, destinationFolderPath, leaf } = params;
  const sourcePrefix = folder.path;
  if (!sourcePrefix.endsWith("/")) {
    return [
      {
        fileRecordId: folder.id,
        path: `${destinationFolderPath}${leaf}`,
      },
    ];
  }

  const destPrefix = `${destinationFolderPath}${leaf}/`;
  const descendants = allRecords.filter(
    (record) =>
      record.id !== folder.id &&
      record.path.startsWith(sourcePrefix) &&
      record.path !== sourcePrefix,
  );

  // Folder markers before files: a copy that lands inside a folder needs the
  // folder to exist, and shallower paths sort before the ones nested in them.
  const isFolder = (record: FileRecord) => record.path.endsWith("/");
  const ordered = [...descendants].sort((a, b) => {
    if (isFolder(a) !== isFolder(b)) return isFolder(a) ? -1 : 1;
    return a.path.length - b.path.length;
  });

  return [
    { fileRecordId: folder.id, path: destPrefix },
    ...ordered.map((record) => ({
      fileRecordId: record.id,
      path: `${destPrefix}${record.path.slice(sourcePrefix.length)}`,
    })),
  ];
}
